/**
 * Pure conversation-resolution helpers, extracted from MessageRepository so the
 * self-message defect that shipped once can be locked down by unit tests
 * (the repository itself needs live Firestore and isn't unit-testable here).
 *
 * The bug: a self-pair (userId === recipientId) made the "find an existing
 * thread that also contains the recipient" lookup match EVERY conversation the
 * user is in, so a self-message was dumped into an arbitrary existing thread
 * and reached the wrong person. These two functions are the single source of
 * truth for both rules.
 */

export interface ConversationLike {
  id: string;
  participants: string[];
}

/** True when both ids are present and identical — a self-pair, never valid. */
export function isSelfPair(a?: string | null, b?: string | null): boolean {
  return !!a && !!b && a === b;
}

/**
 * Returns the id of the FIRST existing conversation that contains recipientId,
 * or null. First-match (not last) is deliberate: the original forEach never
 * broke and silently preferred whichever duplicate thread sorted last.
 *
 * Callers MUST reject self-pairs (isSelfPair) BEFORE calling this — otherwise a
 * self-recipient matches every conversation the user is in.
 */
export function findExistingConversationId(
  conversations: ConversationLike[],
  recipientId: string,
): string | null {
  for (const c of conversations) {
    if (Array.isArray(c.participants) && c.participants.includes(recipientId)) {
      return c.id;
    }
  }
  return null;
}
